import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BriefcaseIcon,
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  CrownIcon,
  PartyPopperIcon,
  PinIcon,
  PlusIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router";

import { CompanyLogoImage } from "@/components";
import { BottleneckCell } from "@/components/BottleneckCell";
import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { EmptyState } from "@/components/EmptyState";
import { JobPinButton } from "@/components/JobPinButton";
import { OptionalTooltip } from "@/components/OptionalTooltip";
import { Row } from "@/components/Row";
import { SortableHeader } from "@/components/SortableHeader";
import { StafferCell } from "@/components/StafferCell";
import { Text } from "@/components/Text";
import { DashboardColumnsPopover } from "@/components/dashboard/DashboardColumnsPopover";
import { DeltaBadge } from "@/components/dashboard/DeltaBadge";
import { ReviewAllButton } from "@/components/dashboard/ReviewAllButton";
import {
  DashboardDifficultyCell,
  DashboardLiquidityCell,
  DashboardPriorityCell,
} from "@/components/dashboard/cells";
import { PriorityCell } from "@/components/portfolio/table/cells/PriorityCell";
import { PulseCell } from "@/components/portfolio/table/cells/PulseCell";
import { Avatar } from "@/components/quick-access/Avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { usePinnedJobs } from "@/contexts/PinnedJobsContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useUpdateSetJobCtdReqs } from "@/hooks/updateSets/useUpdateSetJobCtdReqs";
import {
  ADMIN_USER_IDS,
  ALL_USERS_KEY,
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
  FINALIST_STATUSES,
  jobStatusToBackgroundColor,
  jobStatusToHoverBackgroundColor,
  jobStatusToLabel,
  SUBMISSION_STATUSES,
} from "@/lib/constants";
import { dashboard } from "@/lib/dashboard";
import { jobs } from "@/lib/jobs";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  DashboardHideableColumn,
  DashboardJob,
  DashboardSelectedUser,
  DashboardSortKey,
  DashboardSortState,
  JobPriority,
  JobStatus,
  PipelineNavState,
} from "@/types";

const SKELETON_ROW_COUNT = 8;
const REM_TO_PX = 16;
const DIFFICULTY_FULL_LABEL_MIN_WIDTH_PX = 90;
// Matches the "unassigned" entry TaggableUsersContext seeds its name maps with.
const UNASSIGNED_STAFFER_KEY = "unassigned";
const CTD_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PRIORITY_LABELS: Record<JobPriority, string> = {
  [JobPriority.HIGH]: "High priority",
  [JobPriority.MEDIUM]: "Medium priority",
  [JobPriority.LOW]: "Low priority",
  [JobPriority.BACKBURNERED]: "Backburnered",
};

const sumCtdReqs = (jobs: DashboardJob[]) =>
  jobs.reduce((total, job) => total + (job.ctdReqs ?? 0), 0);

// What the staffer stands to make if their CTD calls land: summed per job, not
// (total reqs × total rate), which would multiply across unrelated jobs.
const sumCtdRevenue = (jobs: DashboardJob[]) =>
  jobs.reduce(
    (total, job) =>
      total + (job.ctdReqs ?? 0) * (job.estimatedRevenueAmount ?? 0),
    0,
  );

const isMuted = (job: DashboardJob) =>
  jobs.isMutedOnDashboard(job.status, job.pausedAt);

const liveGroupJobs = (dashboardJobs: DashboardJob[]) =>
  dashboardJobs.filter((job) => !isMuted(job));

const sumGroupApplied = (
  jobs: DashboardJob[],
  stafferId: DashboardSelectedUser,
) =>
  liveGroupJobs(jobs).reduce(
    (total, job) =>
      total +
      dashboard.countCandidaciesInStatusesForJob(
        job,
        [CandidacyStatus.APPLIED],
        stafferId,
      ),
    0,
  );

const sumGroupCommsToSend = (
  jobs: DashboardJob[],
  stafferId: DashboardSelectedUser,
) =>
  liveGroupJobs(jobs).reduce(
    (total, job) => total + dashboard.countCommsToSendForJob(job, stafferId),
    0,
  );

// Highest priority first inside a staffer's group; jobs with none sort last.
const PRIORITY_GROUP_RANK: Record<JobPriority, number> = {
  [JobPriority.HIGH]: 4,
  [JobPriority.MEDIUM]: 3,
  [JobPriority.LOW]: 2,
  [JobPriority.BACKBURNERED]: 1,
};

// Per-column minimum widths (rem). A column never shrinks below its minimum;
// any table width beyond the summed minimums is divvied out evenly so the
// visible columns fill the table instead of leaving trailing empty space.
const COLUMN_MIN_WIDTH_REM = {
  jobs: 16,
  liquidity: 7,
  priority: 5.5,
  apps: 6,
  commsToSend: 7,
  submissions: 7,
  finalists: 6,
  search: 8,
  difficulty: 5,
  bottleneck: 5.5,
  staffer: 7,
  ctd: 4.5,
  revenue: 8.5,
  ctdRev: 8.5,
  pulse: 20,
  columns: 2.5,
};

export const DashboardTable = ({
  isGroupedByStaffer,
  onGroupedByStafferChange,
  sort,
  sortedJobs,
  onSortChange,
  onReviewJob,
  onReviewSearchJob,
  isLoading,
  selectedUserId,
}: {
  isGroupedByStaffer: boolean;
  onGroupedByStafferChange: (next: boolean) => void;
  sort: DashboardSortState | null;
  sortedJobs: DashboardJob[];
  onSortChange: (next: DashboardSortState | null) => void;
  onReviewJob: (job: DashboardJob, groupStafferId?: number) => void;
  onReviewSearchJob: (job: DashboardJob) => void;
  isLoading: boolean;
  selectedUserId: DashboardSelectedUser;
}) => {
  const navigate = useNavigate();
  const currentUser = useGetCurrentUser();
  const { isPinned } = usePinnedJobs();
  const { userNameMap } = useTaggableUsers();
  const { data: userSettings } = useGetUserSettings();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  // Viewed = the staffer opened this job's search review (here or on the
  // search page); the cell drops its pill background and shows a plain count.
  const [searchReviewViewedJobIds, setSearchReviewViewedJobIds] = useState<
    Set<string>
  >(() => new Set(storage.getSearchReviewViewedJobIds()));

  // Mirror the hidden sets to localStorage so the skeleton header renders the
  // right columns before the settings request resolves (no column flash).
  useEffect(() => {
    if (userSettings) {
      storage.setHiddenDashboardColumns(userSettings.hiddenDashboardColumns);
      storage.setHiddenDashboardAllColumns(
        userSettings.hiddenDashboardAllColumns,
      );
    }
  }, [userSettings]);

  // Track the scroll container's width so leftover space can be divvied evenly.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      setContainerWidth(el.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const hiddenColumns =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    userSettings?.hiddenDashboardColumns || storage.getHiddenDashboardColumns();
  const hiddenAllColumns =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    userSettings?.hiddenDashboardAllColumns ||
    storage.getHiddenDashboardAllColumns();
  const isAllView = selectedUserId === ALL_USERS_KEY;
  // Each view has its own hidden set; the All view's covers every column but
  // Jobs, the per-staffer view's only the original hideable columns.
  const isColumnVisible = (column: DashboardHideableColumn) =>
    !(isAllView ? hiddenAllColumns : hiddenColumns).includes(column);
  const showDifficulty = isColumnVisible(DashboardHideableColumn.DIFFICULTY);
  const showBottleneck = isColumnVisible(DashboardHideableColumn.BOTTLENECK);
  const showStaffer = isColumnVisible(DashboardHideableColumn.STAFFER);
  const showLiquidity = isColumnVisible(DashboardHideableColumn.LIQUIDITY);
  const showPriority = isColumnVisible(DashboardHideableColumn.PRIORITY);
  const showApps = isColumnVisible(DashboardHideableColumn.APPS);
  const showCommsToSend = isColumnVisible(
    DashboardHideableColumn.COMMS_TO_SEND,
  );
  const showSubmissions = isColumnVisible(DashboardHideableColumn.SUBMISSIONS);
  const showFinalists = isColumnVisible(DashboardHideableColumn.FINALISTS);
  const showPulse = isColumnVisible(DashboardHideableColumn.PULSE);
  const showCtd = isColumnVisible(DashboardHideableColumn.CTD);
  const showCtdRev = isColumnVisible(DashboardHideableColumn.CTD_REV);
  // Reassigning reqs and setting CTD are leadership-only; everyone else reads.
  const isAdmin = ADMIN_USER_IDS.includes(currentUser?.id ?? 0);
  // Search top candidates are per-staffer; the All view has no user to scope
  // them to, so the column is hidden there.
  const showSearch = !isAllView;
  // On the All view, per-req revenue gives way to the CTD Rev the staffer can
  // actually land; the per-staffer view still leads with the raw rate.
  const showRevenue = !isAllView;
  // Visible columns in render order, each with its minimum width. Hiding a
  // column drops it here, which frees its width for the rest.
  const visibleColumns = [
    { key: "jobs", minWidthRem: COLUMN_MIN_WIDTH_REM.jobs },
    ...(showLiquidity
      ? [{ key: "liquidity", minWidthRem: COLUMN_MIN_WIDTH_REM.liquidity }]
      : []),
    ...(showPriority
      ? [{ key: "priority", minWidthRem: COLUMN_MIN_WIDTH_REM.priority }]
      : []),
    ...(showApps
      ? [{ key: "apps", minWidthRem: COLUMN_MIN_WIDTH_REM.apps }]
      : []),
    ...(showCommsToSend
      ? [
          {
            key: "comms-to-send",
            minWidthRem: COLUMN_MIN_WIDTH_REM.commsToSend,
          },
        ]
      : []),
    ...(showSubmissions
      ? [{ key: "submissions", minWidthRem: COLUMN_MIN_WIDTH_REM.submissions }]
      : []),
    ...(showFinalists
      ? [{ key: "finalists", minWidthRem: COLUMN_MIN_WIDTH_REM.finalists }]
      : []),
    ...(showSearch
      ? [{ key: "search", minWidthRem: COLUMN_MIN_WIDTH_REM.search }]
      : []),
    ...(showDifficulty
      ? [{ key: "difficulty", minWidthRem: COLUMN_MIN_WIDTH_REM.difficulty }]
      : []),
    ...(showBottleneck
      ? [{ key: "bottleneck", minWidthRem: COLUMN_MIN_WIDTH_REM.bottleneck }]
      : []),
    ...(showStaffer
      ? [{ key: "staffer", minWidthRem: COLUMN_MIN_WIDTH_REM.staffer }]
      : []),
    ...(showCtd ? [{ key: "ctd", minWidthRem: COLUMN_MIN_WIDTH_REM.ctd }] : []),
    ...(showRevenue
      ? [{ key: "revenue", minWidthRem: COLUMN_MIN_WIDTH_REM.revenue }]
      : []),
    ...(showCtdRev
      ? [{ key: "ctd-rev", minWidthRem: COLUMN_MIN_WIDTH_REM.ctdRev }]
      : []),
    ...(showPulse
      ? [{ key: "pulse", minWidthRem: COLUMN_MIN_WIDTH_REM.pulse }]
      : [{ key: "columns", minWidthRem: COLUMN_MIN_WIDTH_REM.columns }]),
  ];
  const visibleColumnCount = visibleColumns.length;
  const tableMinWidthRem = visibleColumns.reduce(
    (sum, column) => sum + column.minWidthRem,
    0,
  );

  // Extra width beyond the summed minimums, split evenly across the columns.
  const leftoverPx =
    containerWidth === null ? 0 : containerWidth - tableMinWidthRem * REM_TO_PX;
  const extraPerColumnPx = Math.max(
    0,
    Math.floor(leftoverPx / visibleColumnCount),
  );
  const columnWidthPx = (minWidthRem: number) =>
    minWidthRem * REM_TO_PX + extraPerColumnPx;
  const difficultyLabel =
    columnWidthPx(COLUMN_MIN_WIDTH_REM.difficulty) >=
    DIFFICULTY_FULL_LABEL_MIN_WIDTH_PX
      ? "Difficulty"
      : "Diff";

  const handleSort = (key: DashboardSortKey) => {
    // Sorting is a flat-list operation, so picking one drops out of grouping.
    onGroupedByStafferChange(false);
    const next = dashboard.getNextSortState(sort, key);
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.DASHBOARD,
      "Sort by",
      next ? `${next.key} ${next.direction}` : "none",
    );
    onSortChange(next);
  };

  const navigateToSearch = (job: DashboardJob) => {
    mixpanelUtils.trackButtonClick(
      "Dashboard search candidates",
      MIXPANEL_SCREENS.DASHBOARD,
      { job_id: job.id, company_id: job.companyId },
    );
    void navigate(dashboard.jobSearchPath(job), {
      state: { from: "dashboard" },
    });
  };

  const handleReviewSearch = (job: DashboardJob) => {
    mixpanelUtils.trackButtonClick(
      "Dashboard review search candidates",
      MIXPANEL_SCREENS.DASHBOARD,
      { job_id: job.id, company_id: job.companyId },
    );
    storage.setSearchReviewViewed(job.id);
    setSearchReviewViewedJobIds(new Set(storage.getSearchReviewViewedJobIds()));
    onReviewSearchJob(job);
  };

  const navigateToPipeline = (job: DashboardJob) => {
    mixpanelUtils.trackRowClick("Dashboard job", MIXPANEL_SCREENS.DASHBOARD, {
      job_id: job.id,
      company_id: job.companyId,
    });
    void navigate(dashboard.jobPipelinePath(job), {
      state: { from: "dashboard" },
    });
  };

  // Whose pulse a row shows: the selected staffer's own on a personal
  // dashboard, the section staffer's when the All view is grouped, otherwise
  // the latest note from anyone.
  const getPulseForRow = (
    job: DashboardJob,
    groupStafferId?: number | string,
  ) => {
    if (!isAllView) {
      return typeof selectedUserId === "number"
        ? dashboard.latestPulseForUser(job, selectedUserId)
        : null;
    }
    if (typeof groupStafferId === "number") {
      return dashboard.latestPulseForUser(job, groupStafferId);
    }
    return dashboard.latestPulse(job);
  };

  const getSearchReviewCount = (job: DashboardJob) => {
    const interestedTalentCount = job.searchInterestTalentIds.length;
    const shortlistedCandidateCount =
      dashboard.countCandidaciesInStatusesForJob(
        job,
        [CandidacyStatus.SHORTLIST],
        selectedUserId,
      );

    return interestedTalentCount + shortlistedCandidateCount;
  };

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Column className="gap-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-28" />
            </Column>
          </TableCell>
          {Array.from({ length: visibleColumnCount - 1 }, (_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }
    if (sortedJobs.length === 0) {
      return (
        <TableRow>
          <TableCell className="p-0" colSpan={visibleColumnCount}>
            <EmptyState
              className="rounded-b-lg bg-slate-50 py-12"
              description="Jobs assigned to this staffer will appear here."
              headerClassName="text-xl"
              Icon={BriefcaseIcon}
              title="No jobs to show"
            />
          </TableCell>
        </TableRow>
      );
    }

    if (!isGroupedByStaffer) return sortedJobs.map((job) => renderJobRow(job));

    return buildStafferGroups().flatMap((group) => {
      // The unassigned bucket's jobs have no owner, so its totals count every
      // candidacy on them rather than one staffer's.
      const groupStafferId: DashboardSelectedUser =
        typeof group.id === "number" ? group.id : ALL_USERS_KEY;

      return [
        <TableRow
          key={`group-${group.id.toString()}`}
          className="bg-slate-100 hover:bg-slate-100"
        >
          {/* Sticky on the cells, not the row — Chrome ignores sticky on <tr>.
            Each group header pins below the column header until the next
            group's header pushes it out. */}
          {visibleColumns.map((column) => (
            <TableCell
              key={column.key}
              className="sticky top-12 z-10 h-9 bg-slate-100 !px-3 !py-0"
            >
              {column.key === "jobs" ? (
                <Text className="truncate text-[13px] font-medium text-slate-700">
                  {group.label}
                </Text>
              ) : null}
              {column.key === "apps" ? (
                <GroupTaskTotal
                  total={sumGroupApplied(group.jobs, groupStafferId)}
                />
              ) : null}
              {column.key === "comms-to-send" ? (
                <GroupTaskTotal
                  total={sumGroupCommsToSend(group.jobs, groupStafferId)}
                />
              ) : null}
              {column.key === "ctd" ? (
                <Text className="text-[13px] font-medium tabular-nums text-slate-700">
                  {sumCtdReqs(group.jobs) || "—"}
                </Text>
              ) : null}
              {column.key === "ctd-rev" ? (
                <Text className="text-[13px] font-medium tabular-nums text-slate-700">
                  {formatting.currency(sumCtdRevenue(group.jobs))}
                </Text>
              ) : null}
            </TableCell>
          ))}
        </TableRow>,
        // A job owned by several staffers is listed under each of them, so the
        // row key has to carry the group too.
        ...group.jobs.map((job) => renderJobRow(job, group.id)),
      ];
    });
  };

  // On the All view the Staffer column groups instead of sorts — there's no
  // single staffer per row to sort by, and grouping is what the view is for.
  const renderStafferHeader = () => {
    if (selectedUserId !== ALL_USERS_KEY) {
      return (
        <SortableHeader
          sortKey="staffer"
          label="Staffer"
          sort={sort}
          onSort={handleSort}
        />
      );
    }

    return (
      <TableHead
        className="cursor-pointer select-none hover:bg-slate-100"
        onClick={() => {
          onGroupedByStafferChange(!isGroupedByStaffer);
        }}
      >
        <Row className="min-w-0 items-center gap-1.5">
          <Text className="min-w-0 truncate font-medium text-slate-500">
            Staffer
          </Text>
          {isGroupedByStaffer ? (
            <CheckIcon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          ) : null}
        </Row>
      </TableHead>
    );
  };

  // Groups the visible jobs by owner for the All view: alphabetical by staffer,
  // unassigned last, and highest-priority jobs first within each group.
  const buildStafferGroups = () => {
    const jobsByStafferId = new Map<number | string, DashboardJob[]>();
    sortedJobs.forEach((job) => {
      const stafferIds: (number | string)[] =
        job.ownerUserIds.length > 0
          ? job.ownerUserIds
          : [UNASSIGNED_STAFFER_KEY];
      stafferIds.forEach((stafferId) => {
        const existing = jobsByStafferId.get(stafferId);
        if (existing) {
          existing.push(job);
          return;
        }
        jobsByStafferId.set(stafferId, [job]);
      });
    });

    const groups = [...jobsByStafferId].map(([id, jobs]) => {
      const pinRank = (job: DashboardJob) =>
        typeof id === "number" && job.pinnedUserIds.includes(id) ? 0 : 1;
      // Paused, covered, and draft jobs sink to the bottom of the staffer's
      // section, unless they pinned them.
      const pausedRank = (job: DashboardJob) =>
        isMuted(job) && pinRank(job) === 1 ? 1 : 0;
      return {
        id,
        label: userNameMap[id] ?? "Unknown staffer",
        jobs: [...jobs].sort((a, b) => {
          const pinDiff = pinRank(a) - pinRank(b);
          if (pinDiff !== 0) return pinDiff;
          const pausedDiff = pausedRank(a) - pausedRank(b);
          if (pausedDiff !== 0) return pausedDiff;
          const aRank = a.priority ? PRIORITY_GROUP_RANK[a.priority] : 0;
          const bRank = b.priority ? PRIORITY_GROUP_RANK[b.priority] : 0;
          if (aRank !== bRank) return bRank - aRank;
          return a.title.localeCompare(b.title);
        }),
      };
    });

    return groups.sort((a, b) => {
      if (a.id === UNASSIGNED_STAFFER_KEY) return 1;
      if (b.id === UNASSIGNED_STAFFER_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  };

  const renderJobRow = (
    job: DashboardJob,
    groupStafferId?: number | string,
  ) => {
    const isPinnedByGroupStaffer =
      typeof groupStafferId === "number" &&
      job.pinnedUserIds.includes(groupStafferId);
    const pulse = getPulseForRow(job, groupStafferId);
    // Under a staffer's section the counts are that staffer's, not the job's —
    // otherwise a row shows candidacies its section owner doesn't own.
    const rowStafferId =
      typeof groupStafferId === "number" ? groupStafferId : undefined;
    // Attribute the note only when the row isn't already scoped to one
    // staffer — i.e. the All view's ungrouped rows and its unassigned group.
    const pulseAuthorName =
      isAllView && typeof groupStafferId !== "number" && pulse
        ? userNameMap[pulse.userId]
        : null;

    return (
      <TableRow
        key={
          groupStafferId === undefined
            ? job.id
            : `${groupStafferId.toString()}-${job.id}`
        }
        className={cn(
          "group cursor-pointer",
          isMuted(job) &&
            !isPinned(job.id) &&
            !isPinnedByGroupStaffer &&
            "opacity-60",
        )}
        onClick={() => {
          navigateToPipeline(job);
        }}
      >
        <JobCell isPinnedByGroupStaffer={isPinnedByGroupStaffer} job={job} />
        {showLiquidity ? (
          <TableCell className="overflow-hidden whitespace-nowrap align-middle">
            <DashboardLiquidityCell
              breakdown={job.liquidityBreakdown}
              liquidity={job.liquidity}
              showGuidance
            />
          </TableCell>
        ) : null}
        {showPriority ? (
          <TableCell
            className="overflow-hidden whitespace-nowrap !px-1 align-middle"
            onClick={(event) => {
              if (isAdmin) event.stopPropagation();
            }}
          >
            <OptionalTooltip
              text={
                job.priority ? PRIORITY_LABELS[job.priority] : "No priority"
              }
              trackingLabel="Dashboard priority tooltip"
            >
              <Row className="w-fit">
                {isAdmin ? (
                  <PriorityCell iconOnly job={job} />
                ) : (
                  <DashboardPriorityCell priority={job.priority} />
                )}
              </Row>
            </OptionalTooltip>
          </TableCell>
        ) : null}
        {showApps ? (
          <AppliedCell
            groupStafferId={rowStafferId}
            job={job}
            onReview={onReviewJob}
            selectedUserId={selectedUserId}
          />
        ) : null}
        {showCommsToSend ? (
          <CommsToSendCell
            groupStafferId={rowStafferId}
            job={job}
            selectedUserId={selectedUserId}
          />
        ) : null}
        {showSubmissions ? (
          <CandidacyStatusCountCell job={job} statuses={SUBMISSION_STATUSES} />
        ) : null}
        {showFinalists ? (
          <CandidacyStatusCountCell job={job} statuses={FINALIST_STATUSES} />
        ) : null}
        {showSearch ? (
          <TableCell className="group/search relative overflow-hidden whitespace-nowrap !px-2 align-middle">
            <DashboardSearchCell
              count={getSearchReviewCount(job)}
              isRead={searchReviewViewedJobIds.has(job.id)}
              onClick={() => {
                navigateToSearch(job);
              }}
              onReview={() => {
                handleReviewSearch(job);
              }}
            />
          </TableCell>
        ) : null}
        {showDifficulty ? (
          <TableCell className="overflow-hidden whitespace-nowrap !px-2 align-middle">
            <DashboardDifficultyCell difficulty={job.difficulty} />
          </TableCell>
        ) : null}
        {showBottleneck ? (
          <TableCell
            className="overflow-hidden px-2 text-left align-middle"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Row className="justify-start">
              <BottleneckCell
                bottleneck={job.bottleneck}
                companyId={job.companyId}
                jobId={job.id}
              />
            </Row>
          </TableCell>
        ) : null}
        {showStaffer ? (
          <StafferCell
            isEditable={isAdmin}
            jobId={job.id}
            ownerUserIds={job.ownerUserIds}
          />
        ) : null}
        {showCtd ? <CtdCell isEditable={isAdmin} job={job} /> : null}
        {showRevenue ? <RevenueCell job={job} /> : null}
        {showCtdRev ? <CtdRevCell job={job} /> : null}
        {showPulse ? (
          <TableCell
            className="overflow-hidden align-middle"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <PulseCell
              authorName={pulseAuthorName}
              job={{
                ...job,
                pulseDescription: pulse?.description ?? null,
                pulseCreatedAt: pulse?.createdAt ?? null,
              }}
            />
          </TableCell>
        ) : (
          <TableCell />
        )}
      </TableRow>
    );
  };

  const render = () => (
    <Column className="flex h-full min-h-0 min-w-0 flex-col">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden shadow-none">
        <div
          ref={scrollRef}
          className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto"
        >
          {/* table-fixed makes the <col> widths authoritative. Widths are the
              per-column minimums plus an even share of any leftover width; the
              table min-width forces x-scroll instead of squashing below them. */}
          <table
            className="w-full table-fixed border-separate border-spacing-0 text-sm"
            style={{ minWidth: `${tableMinWidthRem.toString()}rem` }}
          >
            <colgroup>
              {visibleColumns.map((column) => (
                <col
                  key={column.key}
                  style={{
                    width: `${columnWidthPx(column.minWidthRem).toString()}px`,
                  }}
                />
              ))}
            </colgroup>
            <TableHeader className="sticky top-0 z-20 bg-slate-50 [&_th]:h-12 [&_th]:overflow-hidden [&_th]:border-b [&_th]:border-slate-200 [&_th]:px-3 [&_th]:py-0 [&_tr_th:first-child]:rounded-tl-lg [&_tr_th:last-child]:rounded-tr-lg">
              <TableRow>
                <SortableHeader
                  sortKey="jobs"
                  label="Jobs"
                  sort={sort}
                  onSort={handleSort}
                />
                {showLiquidity ? (
                  <TableHead>
                    <Text className="whitespace-nowrap font-medium text-slate-500">
                      Liquidity
                    </Text>
                  </TableHead>
                ) : null}
                {showPriority && (
                  <SortableHeader
                    className="!px-1"
                    sortKey="priority"
                    label="Priority"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showApps && (
                  <SortableHeader
                    className="!px-2"
                    sortKey="applied"
                    label="Apps"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showCommsToSend && (
                  <SortableHeader
                    className="!px-2"
                    sortKey="commsToSend"
                    label="Comms to Send"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showSubmissions && (
                  <SortableHeader
                    sortKey="submissions"
                    label="Submissions"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showFinalists && (
                  <SortableHeader
                    sortKey="finalists"
                    label="Finalists"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showSearch && (
                  <TableHead>
                    <Row className="items-center justify-center">
                      <Text className="whitespace-nowrap font-medium text-slate-500">
                        Search
                      </Text>
                    </Row>
                  </TableHead>
                )}
                {showDifficulty && (
                  <SortableHeader
                    className="!px-2"
                    sortKey="difficulty"
                    label={difficultyLabel}
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showBottleneck && (
                  <SortableHeader
                    className="!px-2"
                    sortKey="bottleneck"
                    label="Bottleneck"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showStaffer && renderStafferHeader()}
                {showCtd && (
                  <SortableHeader
                    sortKey="ctd"
                    label="CTD"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showRevenue && (
                  <SortableHeader
                    sortKey="revenue"
                    label="Rev. per Req"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showCtdRev && (
                  <SortableHeader
                    sortKey="ctdRev"
                    label="CTD Rev"
                    sort={sort}
                    onSort={handleSort}
                  />
                )}
                {showPulse ? (
                  <TableHead>
                    <Row className="items-center justify-between gap-1">
                      <Text className="whitespace-nowrap font-medium text-slate-500">
                        Pulse
                      </Text>
                      <DashboardColumnsPopover isAllView={isAllView} />
                    </Row>
                  </TableHead>
                ) : (
                  <TableHead className="!px-1">
                    <Row className="items-center justify-end">
                      <DashboardColumnsPopover isAllView={isAllView} />
                    </Row>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody className="[&_td]:overflow-hidden [&_td]:!px-3 [&_td]:!py-3 [&_tr:last-child_td:first-child]:rounded-bl-lg [&_tr:last-child_td:last-child]:rounded-br-lg [&_tr:last-child_td]:border-b-0 [&_tr_td]:border-b [&_tr_td]:border-slate-100">
              {renderBody()}
            </TableBody>
          </table>
        </div>
      </Card>
    </Column>
  );

  return render();
};

// showLogo is off in the minimized rail, where the 40px tile would swallow the
// column's width.
export const JobCell = ({
  isPinnedByGroupStaffer = false,
  job,
  showLogo = true,
}: {
  isPinnedByGroupStaffer?: boolean;
  job: DashboardJob;
  showLogo?: boolean;
}) => {
  /* Render */
  const renderLogo = () => (
    <Row className="group/logo relative h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
      {job.companySmallLogoUrl || job.companyLogoUrl ? (
        <CompanyLogoImage companyName={job.companyName} logoUrl={job.companyLogoUrl} smallLogoUrl={job.companySmallLogoUrl} fallback={<Building2Icon className="h-5 w-5 text-slate-400" />} />
      ) : (
        <Building2Icon className="h-5 w-5 text-slate-400" />
      )}
      {isPinnedByGroupStaffer ? (
        <Center className="absolute inset-0 bg-black/35 text-white">
          <PinIcon className="h-4 w-4 fill-current" />
        </Center>
      ) : (
        <JobPinButton
          companyId={job.companyId}
          jobId={job.id}
          screen={MIXPANEL_SCREENS.DASHBOARD}
          variant="logo"
        />
      )}
    </Row>
  );

  const render = () => (
    <TableCell>
      <Row className="min-w-0 items-center gap-3">
        {showLogo ? renderLogo() : null}
        <Column className="min-w-0 gap-0.5">
          <Row className="min-w-0 items-center gap-1.5">
            <Text className="truncate font-medium text-slate-900">
              {job.title}
            </Text>
            <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-slate-100 px-1.5 text-xs font-medium text-slate-600">
              {job.candidacies.length}
            </span>
            {job.status === JobStatus.DRAFT && (
              <Badge
                className={cn(
                  jobStatusToBackgroundColor[JobStatus.DRAFT],
                  jobStatusToHoverBackgroundColor[JobStatus.DRAFT],
                  "h-5 shrink-0 text-[10px]",
                )}
                variant="secondary"
              >
                {jobStatusToLabel[JobStatus.DRAFT]}
              </Badge>
            )}
            {job.pausedAt && (
              <OptionalTooltip
                text={jobs.pausedTooltip(
                  job.pausedAt,
                  job.pauseReason,
                  job.pauseType,
                )}
                trackingLabel="Paused job tooltip"
              >
                {/* Row wrapper: Badge doesn't forward refs, which the tooltip
                    trigger's asChild requires. */}
                <Row className="shrink-0">
                  <Badge
                    className={cn(
                      jobs.pauseBackgroundColor(job.pauseType),
                      jobs.pauseHoverBackgroundColor(job.pauseType),
                      "h-5 text-[10px]",
                    )}
                    variant="secondary"
                  >
                    {jobs.pauseLabel(job.pauseType)}
                  </Badge>
                </Row>
              </OptionalTooltip>
            )}
            {/* The logo tile carries the pin; the minimized rail has no tile,
                so it keeps the inline one. */}
            {showLogo ? null : (
              <JobPinButton
                companyId={job.companyId}
                jobId={job.id}
                screen={MIXPANEL_SCREENS.DASHBOARD}
              />
            )}
          </Row>
          <Text className="truncate text-[13px] font-normal text-slate-500">
            {job.companyName}
          </Text>
        </Column>
      </Row>
    </TableCell>
  );

  return render();
};

const AppliedCell = ({
  groupStafferId,
  job,
  onReview,
  selectedUserId,
}: {
  // Set on the All view's grouped rows: whose candidacies this row counts.
  groupStafferId?: number;
  job: DashboardJob;
  onReview: (job: DashboardJob, groupStafferId?: number) => void;
  selectedUserId: DashboardSelectedUser;
}) => {
  const countUserId = groupStafferId ?? selectedUserId;
  const appliedCount = dashboard.countCandidaciesInStatusesForJob(
    job,
    [CandidacyStatus.APPLIED],
    countUserId,
  );
  // What the review button actually opens; can be fewer than the count when
  // some applicants are still out with Stevie.
  const reviewableCount = dashboard.reviewableAppliedForJob(
    job,
    countUserId,
  ).length;
  // Delta ("since you last viewed") is per-staffer; the All view has no owner
  // to anchor it to, so it's hidden there.
  const isAllView = selectedUserId === ALL_USERS_KEY;
  const delta = isAllView
    ? 0
    : dashboard.getDelta(
        appliedCount,
        dashboard.appliedSnapshotForView(job, selectedUserId),
      );

  const render = () => {
    if (appliedCount === 0) {
      return <ZeroCountTaskCell />;
    }

    return (
      <TableCell className="group/applied relative !px-2 text-left">
        <Row className="items-center justify-start gap-1.5 group-hover/applied:invisible">
          <Text className="font-medium tabular-nums text-slate-900">
            {appliedCount}
          </Text>
          {isAllView ? null : <DeltaBadge delta={delta} />}
        </Row>
        {reviewableCount > 0 ? (
          <Row className="invisible absolute inset-0 items-center justify-start group-hover/applied:visible">
            <ReviewAllButton
              count={reviewableCount}
              onClick={(e) => {
                e.stopPropagation();
                mixpanelUtils.trackButtonClick(
                  "Review job applied",
                  MIXPANEL_SCREENS.DASHBOARD,
                  { job_id: job.id },
                );
                onReview(job, groupStafferId);
              }}
              className="h-8 gap-1 px-2 text-[11px]"
            />
          </Row>
        ) : null}
      </TableCell>
    );
  };

  return render();
};

const CommsToSendCell = ({
  groupStafferId,
  job,
  selectedUserId,
}: {
  // Set on the All view's grouped rows: whose candidacies this row counts.
  groupStafferId?: number;
  job: DashboardJob;
  selectedUserId: DashboardSelectedUser;
}) => {
  const navigate = useNavigate();
  const countUserId = groupStafferId ?? selectedUserId;
  const count = dashboard.countCommsToSendForJob(job, countUserId);
  // Delta ("since you last viewed") is per-staffer; the All view has no owner
  // to anchor it to, so it's hidden there.
  const isAllView = selectedUserId === ALL_USERS_KEY;
  const delta = isAllView
    ? 0
    : dashboard.getDelta(
        count,
        dashboard.commsToSendSnapshotForView(job, selectedUserId),
      );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    mixpanelUtils.trackButtonClick(
      "Open job — comms to send",
      MIXPANEL_SCREENS.DASHBOARD,
      {
        job_id: job.id,
        company_id: job.companyId,
        job_title: job.title,
        company_name: job.companyName,
        comms_to_send_count: count,
        job_priority: job.priority,
      },
    );
    // Land the pipeline on the same staffer view the count was computed for.
    const navState: PipelineNavState = {
      onlyCommsToSend: true,
      stafferView: dashboard.viewUserForJob(job, countUserId),
    };
    void navigate(dashboard.jobPipelinePath(job), { state: navState });
  };

  const render = () => {
    if (count === 0) {
      return <ZeroCountTaskCell />;
    }

    return (
      <TableCell className="!px-2 text-left align-middle">
        <Row className="items-center justify-start gap-1.5">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClick}
            className="h-8 w-fit shrink-0 px-2 tabular-nums text-slate-700"
          >
            {count}
          </Button>
          <DeltaBadge delta={delta} />
        </Row>
      </TableCell>
    );
  };

  return render();
};

// A staffer's group-header total: zero means their pipeline is clear, so it
// gets the same party popper an empty job row gets.
const GroupTaskTotal = ({ total }: { total: number }) => {
  const render = () => {
    if (total === 0) {
      return (
        <Row className="items-center justify-start gap-1">
          <PartyPopperIcon className="h-3.5 w-3.5 text-green-600" />
          <Text className="text-[13px] font-medium text-slate-300">0</Text>
        </Row>
      );
    }
    return (
      <Text className="text-[13px] font-medium tabular-nums text-slate-700">
        {total}
      </Text>
    );
  };

  return render();
};

const ZeroCountTaskCell = () => (
  <TableCell className="!px-2 text-left align-middle">
    <Row className="items-center justify-start gap-1">
      <PartyPopperIcon className="h-3.5 w-3.5 text-green-600" />
      <Text className="font-medium text-slate-300">0</Text>
    </Row>
  </TableCell>
);

const DashboardSearchCell = ({
  count,
  isRead,
  onClick,
  onReview,
}: {
  count?: number;
  isRead: boolean;
  onClick: () => void;
  onReview: () => void;
}) => {
  const render = () => {
    if (!count) {
      return (
        <Center>
          <Text className="text-sm font-normal text-slate-400">—</Text>
        </Center>
      );
    }

    return (
      <>
        <Center className="group-hover/search:invisible">
          <Row
            className={cn(
              "min-w-8 cursor-pointer items-center justify-center gap-1 rounded-full px-2.5 py-1",
              isRead
                ? "hover:bg-blue-50"
                : "bg-gradient-to-r from-blue-50 to-blue-100 ring-1 ring-blue-100 hover:ring-blue-300",
            )}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            {isRead ? null : <CrownIcon className="h-3 w-3 text-blue-500" />}
            <Text
              className={cn(
                "text-xs",
                isRead
                  ? "font-medium tabular-nums text-slate-900"
                  : "font-semibold text-blue-700",
              )}
            >
              {count}
            </Text>
          </Row>
        </Center>
        <Row className="invisible absolute inset-0 items-center justify-center group-hover/search:visible">
          <ReviewAllButton
            className="h-8 gap-1 px-2 text-[11px]"
            count={count}
            onClick={(event) => {
              event.stopPropagation();
              onReview();
            }}
          />
        </Row>
      </>
    );
  };

  return render();
};

// Count cell shared by the Submissions and Finalists columns; hovering the
// count lists each candidate in the given statuses, with the owning staffer's
// avatar and the status dropdown's stage dot.
const CandidacyStatusCountCell = ({
  job,
  statuses,
}: {
  job: DashboardJob;
  statuses: CandidacyStatus[];
}) => {
  const { userNameMap } = useTaggableUsers();

  const candidacies = job.candidacies.filter((candidacy) =>
    statuses.includes(candidacy.status),
  );

  /* Helpers */
  const ownerInitials = (ownerUserIds: number[]): string => {
    const ownerName = userNameMap[ownerUserIds[0]] as string | undefined;
    if (!ownerName) return "?";
    const parts = ownerName.split(" ");
    return formatting.initials(parts[0], parts[parts.length - 1]);
  };

  /* Render */
  const renderTooltipContent = () => (
    <Column className="gap-2 py-1">
      {candidacies.map((candidacy) => {
        const name =
          `${candidacy.firstName ?? ""} ${candidacy.lastName ?? ""}`.trim();

        return (
          <Row key={candidacy.id} className="items-center gap-2.5">
            <Avatar initials={ownerInitials(candidacy.ownerUserIds)} />
            <Column className="min-w-0 gap-0.5">
              <Link
                target="_blank"
                rel="noreferrer"
                className="w-fit max-w-full"
                to={getCandidacyLink({
                  candidacyId: candidacy.id,
                  companyId: job.companyId,
                  jobId: job.id,
                })}
              >
                <Text className="truncate text-sm font-medium text-slate-900 hover:underline">
                  {name || "Name unavailable"}
                </Text>
              </Link>
              <Row className="items-center gap-1.5">
                <div
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    candidacyStatusToBackgroundColor[candidacy.status],
                  )}
                />
                <Text className="text-xs font-normal text-slate-500">
                  {candidacyStatusToLabel[candidacy.status]}
                </Text>
              </Row>
            </Column>
          </Row>
        );
      })}
    </Column>
  );

  const render = () => {
    if (candidacies.length === 0) {
      return (
        <TableCell>
          <Text className="text-sm font-normal text-slate-300">0</Text>
        </TableCell>
      );
    }

    return (
      <TableCell
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Row wrapper: Text doesn't forward refs, which the tooltip
                trigger's asChild requires. */}
            <Row className="w-fit cursor-default">
              <Text className="font-medium tabular-nums text-slate-900">
                {candidacies.length}
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent className="min-w-56">
            {renderTooltipContent()}
          </TooltipContent>
        </Tooltip>
      </TableCell>
    );
  };

  return render();
};

const CtdCell = ({
  isEditable,
  job,
}: {
  isEditable: boolean;
  job: DashboardJob;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateCtdReqs } = useUpdateSetJobCtdReqs();

  /* Handlers */
  const handleSelect = (ctdReqs: number | null) => {
    if (!job.jobInternalConfigId) return;
    updateCtdReqs({
      companyId: job.companyId,
      jobId: job.id,
      jobInternalConfigId: job.jobInternalConfigId,
      ctdReqs,
    });
  };

  /* Render */
  const renderValue = () => (
    <Text
      className={
        job.ctdReqs === null ? "text-slate-300" : "font-medium text-slate-900"
      }
    >
      {job.ctdReqs ?? "—"}
    </Text>
  );

  const renderTrigger = () => {
    if (job.ctdReqs === null) {
      return (
        <Button
          className="h-6 w-6 shrink-0 rounded-full border border-dashed border-slate-200 bg-slate-50/60 p-0 text-slate-400 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-500"
          type="button"
          variant="outlineSecondary"
        >
          <PlusIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </Button>
      );
    }

    return (
      <Button
        className="h-8 min-w-12 justify-between gap-1 px-0 py-0 font-normal"
        size="sm"
        type="button"
        variant="ghost"
      >
        {renderValue()}
        <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400" />
      </Button>
    );
  };

  const renderOption = (option: number | null) => (
    <DropdownMenuItem
      key={option ?? "clear"}
      className="justify-between"
      onSelect={() => {
        handleSelect(option);
      }}
    >
      {option ?? "—"}
      {job.ctdReqs === option ? (
        <CheckIcon className="h-4 w-4 text-slate-600" />
      ) : null}
    </DropdownMenuItem>
  );

  const render = () => {
    if (!isEditable || !job.jobInternalConfigId) {
      return <TableCell>{renderValue()}</TableCell>;
    }

    return (
      <TableCell
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{renderTrigger()}</DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="max-h-36 min-w-24 overflow-y-auto"
          >
            {[null, ...CTD_OPTIONS].map(renderOption)}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    );
  };

  return render();
};

const CtdRevCell = ({ job }: { job: DashboardJob }) => {
  const ctdRevenue =
    job.ctdReqs === null || job.estimatedRevenueAmount === null
      ? null
      : job.ctdReqs * job.estimatedRevenueAmount;

  /* Render */
  const render = () => (
    <TableCell>
      <Text
        className={
          ctdRevenue === null ? "text-slate-300" : "font-medium text-slate-900"
        }
      >
        {formatting.currency(ctdRevenue)}
      </Text>
    </TableCell>
  );

  return render();
};

const RevenueCell = ({ job }: { job: DashboardJob }) => {
  const render = () => (
    <TableCell>
      <span
        className={
          job.estimatedRevenueAmount === null
            ? "text-slate-300"
            : "font-medium text-slate-900"
        }
      >
        {formatting.currency(job.estimatedRevenueAmount)}
      </span>
    </TableCell>
  );

  return render();
};
